import { StyleSheet, css } from 'aphrodite';
import PropTypes from 'prop-types';
import React, { forwardRef, useState } from 'react';
import { Theme } from '../../config/theme';

const styles = StyleSheet.create({
  messageList: {
    position: 'relative',
    flex: 1,
    overflowY: 'auto',
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    width: '100%',
    height: '100%',
    minHeight: 0,
    WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
    [Theme.mobile_query]: {
      padding: '0.75rem',
      gap: '0.75rem',
      // Add extra padding at bottom to ensure last message is visible
      paddingBottom: '2rem',
    },
  },
  messageContainer: {
    position: 'relative',
    maxWidth: '70%',
    display: 'flex',
    flexDirection: 'column',
    [Theme.mobile_query]: {
      maxWidth: '85%', // Allow more width on mobile
    },
  },
  userMessageContainer: {
    alignSelf: 'flex-end',
  },
  otherMessageContainer: {
    alignSelf: 'flex-start',
  },
  message: {
    padding: '0.75rem',
    borderRadius: '0.5rem',
    position: 'relative',
    [Theme.mobile_query]: {
      padding: '0.625rem 0.875rem',
      borderRadius: '1rem',
    },
  },
  userMessage: {
    backgroundColor: '#0084ff',
    color: '#ffffff',
  },
  otherMessage: {
    backgroundColor: '#ffffff',
  },
  pendingMessage: {
    opacity: 0.6,
  },
  messageTimestamp: {
    fontSize: '0.75rem',
    marginTop: '0.25rem',
    opacity: 0.7,
    [Theme.mobile_query]: {
      fontSize: '0.7rem',
    },
  },
  pendingIndicator: {
    fontSize: '0.75rem',
    marginTop: '0.25rem',
    opacity: 0.9,
    fontStyle: 'italic',
  },
  messageAttachments: {
    marginTop: '0.5rem',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
  },
  attachmentThumbnail: {
    maxWidth: '200px',
    maxHeight: '200px',
    borderRadius: '0.5rem',
    cursor: 'pointer',
    transition: 'opacity 200ms ease',
    ':hover': {
      opacity: 0.9,
    },
    [Theme.mobile_query]: {
      maxWidth: '150px',
      maxHeight: '150px',
    },
  },
  messageActions: {
    position: 'absolute',
    top: '-2px',
    right: '-8px',
    opacity: 0,
    transition: 'opacity 200ms ease',
    [Theme.mobile_query]: {
      opacity: 1,
      top: '4px',
      right: '4px',
    },
  },
  otherMessageActions: {
    right: 'auto',
    left: '100%',
    marginRight: 0,
    marginLeft: '0.5rem',
  },
  messageAction: {
    width: '2rem',
    height: '2rem',
    borderRadius: '50%',
    backgroundColor: '#ffffff',
    border: '1px solid #e0e0e0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    fontSize: '1rem',
    color: '#6c757d',
    transition: 'all 200ms ease',
    ':hover': {
      backgroundColor: '#f8f9fa',
      borderColor: '#ced4da',
    },
  },
  deleteAction: {
    ':hover': {
      backgroundColor: '#fff5f5',
      borderColor: '#dc3545',
      color: '#dc3545',
    },
  },
  reportAction: {
    ':hover': {
      backgroundColor: '#fff7e5',
      borderColor: '#fd7e14',
      color: '#fd7e14',
    },
  },
  messageContainerHover: {
    '&:hover .messageActions': {
      opacity: 1,
    },
  },
  mobileClickOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    zIndex: -1, // Behind messages
    display: 'none',
    [Theme.mobile_query]: {
      display: 'block',
    },
  },
});

export const MessageList = forwardRef(
  ({ messages, onDeleteMessage, onReportMessage, onListClick }, ref) => {
    const [hoveredMessageId, setHoveredMessageId] = useState(null);

    const handleMouseEnter = messageId => {
      setHoveredMessageId(messageId);
    };

    const handleMouseLeave = () => {
      setHoveredMessageId(null);
    };

    return (
      <div ref={ref} className={css(styles.messageList)}>
        {messages.map(message => (
          <div
            key={message.id}
            className={css(
              styles.messageContainer,
              message.sender === 'user'
                ? styles.userMessageContainer
                : styles.otherMessageContainer,
              styles.messageContainerHover,
            )}
            onMouseEnter={() => handleMouseEnter(message.id)}
            onMouseLeave={handleMouseLeave}
          >
            <div
              className={css(
                styles.message,
                message.sender === 'user' ? styles.userMessage : styles.otherMessage,
                message.isPending && styles.pendingMessage,
              )}
            >
              <p>{message.content}</p>
              {message.attachments && message.attachments.length > 0 && (
                <div className={css(styles.messageAttachments)}>
                  {message.attachments.map(attachment => (
                    <a
                      key={attachment.id}
                      href={attachment.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        src={attachment.url}
                        alt={attachment.filename || 'Attachment'}
                        className={css(styles.attachmentThumbnail)}
                      />
                    </a>
                  ))}
                </div>
              )}
              {message.isPending ? (
                <p className={css(styles.pendingIndicator)}>Sending...</p>
              ) : (
                <p className={css(styles.messageTimestamp)}>
                  {message.timestamp.toLocaleString([], {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              )}
            </div>

            {hoveredMessageId === message.id && !message.isPending && (
              <div
                className={css(
                  styles.messageActions,
                  message.sender === 'other' && styles.otherMessageActions,
                )}
              >
                {message.sender === 'user' && (
                  <button
                    type="button"
                    className={css(styles.messageAction, styles.deleteAction)}
                    onClick={() => onDeleteMessage(message.id)}
                    aria-label="Delete message"
                  >
                    <span role="img" aria-label="Delete">
                      🗑️
                    </span>
                  </button>
                )}
                {message.sender === 'other' && (
                  <button
                    type="button"
                    className={css(styles.messageAction, styles.reportAction)}
                    onClick={() => onReportMessage(message)}
                    aria-label="Report message"
                  >
                    <span role="img" aria-label="Report">
                      ⚠️
                    </span>
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
        {/* Mobile click overlay to unfocus input */}
        {Theme.isMobile && onListClick && (
          <button
            type="button"
            onClick={onListClick}
            className={css(styles.mobileClickOverlay)}
            aria-label="Tap to hide keyboard"
          />
        )}
      </div>
    );
  },
);

MessageList.displayName = 'MessageList';

MessageList.propTypes = {
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      content: PropTypes.string.isRequired,
      sender: PropTypes.oneOf(['user', 'other']).isRequired,
      timestamp: PropTypes.instanceOf(Date).isRequired,
      isPending: PropTypes.bool,
      attachments: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number,
          url: PropTypes.string,
          filename: PropTypes.string,
          type: PropTypes.string,
        }),
      ),
    }),
  ).isRequired,
  onDeleteMessage: PropTypes.func,
  onReportMessage: PropTypes.func,
  onListClick: PropTypes.func,
};

MessageList.defaultProps = {
  onDeleteMessage: () => {},
  onReportMessage: () => {},
  onListClick: null,
};



// WEBPACK FOOTER //
// ./src/views/MessengerConversation/message-list.js